import { useState } from "react";
import { addSupplierQuote, editSupplierQuote, disableSupplierQuote, setPartQuote } from "../../models/price-drawer/pricingModel";
import { getCostPriceTier } from "../../models/shared/costPricesModel";
import { baseFromGp as spFromGp, gpFromBase as gpFromSp } from "../../models/price-drawer/quotePricingPolicy";
import { recommendedPricing } from "../../models/price-drawer/quoteEditingPolicy";
export const EMPTY_FORM = { supplier_id: null, price: null, warranty_days: null, delivery_charge: null, gp: null, sp: null };

export function useQuoteEditing({ card, userId, parts, setParts, costPrices, setQuotePricing, notify }) {
  const [addingPartId, setAddingPartId] = useState(null);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addSaving, setAddSaving] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [selectingPartId, setSelectingPartId] = useState(null);
  const [pricingSaves, setPricingSaves] = useState(0);
  // This drawer is reachable from any status (WorkflowTaskDrawer's Parts tab
  // opens it universally). draft/waiting_for_price are the only "normal"
  // editing stages — anything past that (sent for confirmation, payment in
  // flight/done, cancelled, completed, parked) is an override worth flagging.
  const isOverrideStatus = card?.status && card.status !== "draft" && card.status !== "waiting_for_price";
  const [overrideConfirm, setOverrideConfirm] = useState(null); // { title, description, onConfirm } | null
  const [overrideNote, setOverrideNote] = useState("");

  // Wraps `action` so that outside the normal draft/waiting_for_price editing
  // stages, staff see a confirm+optional-note dialog before an override
  // (add/edit/delete/select quote, share quote) proceeds. `action` receives
  // the trimmed note (or null).
  const withOverrideGuard = (title, description, action) => {
    if (!isOverrideStatus) { action(null); return; }
    setOverrideNote("");
    setOverrideConfirm({ title, description, onConfirm: action });
  };


  const handleAddQuote = async (partId, note) => {
    if (!addForm.supplier_id || addForm.price == null) return;
    setAddSaving(true);
    try {
      const res = await addSupplierQuote(partId, {
        supplierId: addForm.supplier_id,
        price: addForm.price,
        warrantyDays: addForm.warranty_days ?? 0,
        deliveryCharge: 0,
        userId,
        ...(note ? { remarks: note } : {}),
      });
      if (res.success) {
        const newQuote = res.data.quote;
        setParts(prev => prev.map(p =>
          p.id === partId
            ? { ...p, supplier_quotes: [...(p.supplier_quotes ?? []), newQuote] }
            : p
        ));
        // The form already computed/holds the GP%/SP pair (auto-calculated from
        // cost, or manually adjusted) — carry it over as-is rather than
        // recomputing, so a manual tweak made just before saving isn't lost.
        const cp = Number(newQuote.quoted_price || addForm.price || 0);
        const gp = addForm.gp ?? 0;
        const sp = addForm.sp ?? cp;
        setQuotePricing(prev => ({ ...prev, [newQuote.id]: { sp, gp, _cp: cp } }));
        setAddingPartId(null);
        setAddForm(EMPTY_FORM);
      }
    } catch {}
    setAddSaving(false);
  };

  const handleEditQuote = async (quoteId, partId, note) => {
    if (editForm.price == null) return;
    setEditSaving(true);
    try {
      const res = await editSupplierQuote(quoteId, {
        supplierId: editForm.supplier_id,
        price: editForm.price,
        deliveryCharge: 0,
        warrantyDays: editForm.warranty_days ?? 0,
        userId,
        ...(note ? { remarks: note } : {}),
      });
      if (res.success) {
        const updated = res.data;
        setParts(prev => prev.map(p => ({
          ...p,
          supplier_quotes: (p.supplier_quotes ?? []).map(q =>
            q.id === quoteId ? { ...q, ...updated } : q
          ),
        })));
        // Same rule as add: trust the form's own GP%/SP rather than recomputing.
        const newCp = Number(updated.quoted_price || editForm.price || 0);
        const gp = editForm.gp ?? 0;
        const sp = editForm.sp ?? newCp;
        setQuotePricing(prev => ({ ...prev, [quoteId]: { sp, gp, _cp: newCp } }));
        // If this quote is already the part's selected quote, its selling price
        // drives what the customer sees — persist it now, otherwise editing an
        // already-selected quote's price silently never reaches query_parts.
        const part = parts.find(p => p.id === partId);
        if (part?.selected_quote_id === quoteId) patchSellingPrice(partId, quoteId, sp);
        setEditingQuoteId(null);
        setEditForm(EMPTY_FORM);
      }
    } catch {}
    setEditSaving(false);
  };

  const onDeleteQuote_original = async (quoteId, partId) => {
    try {
      const res = await disableSupplierQuote(quoteId, userId);
      if (res.success) {
        setParts(prev => prev.map(p =>
          p.id === partId
            ? { ...p, supplier_quotes: (p.supplier_quotes ?? []).filter(q => q.id !== quoteId) }
            : p
        ));
      }
    } catch {}
  };

  const handleSelectQuote = async (partId, quoteId) => {
    setPricingSaves(count => count + 1);
    setSelectingPartId(partId);
    try {
      const part = parts.find(p => p.id === partId);
      const newQuoteId = part?.selected_quote_id === quoteId ? null : quoteId;
      const quote = part?.supplier_quotes?.find(row => row.id === newQuoteId);
      const recommendation = newQuoteId ? recommendedPricing(costPrices, Number(quote?.quoted_price || 0)) : null;
      const sellingPrice = recommendation?.sp ?? null;
      if (newQuoteId) setQuotePricing(previous => ({ ...previous, [newQuoteId]: { ...recommendation, _cp: Number(quote?.quoted_price || 0) } }));
      const res = await setPartQuote(partId, { selectedQuoteId: newQuoteId, sellingPrice, userId });
      if (res.success) {
        setParts(prev => prev.map(p =>
          p.id === partId
            ? { ...p, selected_quote_id: newQuoteId, selling_price: sellingPrice }
            : p
        ));
      }
    } catch {} finally { setPricingSaves(count => Math.max(0, count - 1)); }
    setSelectingPartId(null);
  };


  const patchSellingPrice = (partId, quoteId, sp) => {
    setPricingSaves(count => count + 1);
    const part = parts.find(p => p.id === partId);
    setPartQuote(partId, { selectedQuoteId: quoteId, sellingPrice: sp, userId }).then(res => {
      if (res.success) {
        setParts(prev => prev.map(p =>
          p.id === partId ? { ...p, selling_price: sp } : p
        ));
        if (part?.quoted_final_price != null && Number(part.quoted_sp_snapshot) !== Number(sp)) {
          notify.warning("This part was already quoted to the customer — the invoice will keep using the last-quoted price until you re-share this part's quote.");
        }
      }
    }).catch(() => {}).finally(() => setPricingSaves(count => Math.max(0, count - 1)));
  };

  const handleCalculateAll = () => {
    if (!costPrices.length) return;
    const toSave = []; // [{ partId, quoteId, sp }] for already-selected quotes
    setQuotePricing(prev => {
      const next = { ...prev };
      parts.forEach(p => {
        (p.supplier_quotes ?? []).forEach(q => {
          if (next[q.id]) return; // skip already-priced (preserve manual edits)
          const cp = Number(q.quoted_price || 0);
          const tier = getCostPriceTier(costPrices, cp);
          const gp = tier ? Number(tier.GP_Percentage) : 0;
          const sp = spFromGp(cp, gp);
          next[q.id] = { sp, gp, _cp: cp };
          if (p.selected_quote_id === q.id) toSave.push({ partId: p.id, quoteId: q.id, sp });
        });
      });
      return next;
    });
    toSave.forEach(({ partId, quoteId, sp }) => patchSellingPrice(partId, quoteId, sp));
  };

  const handleRoundAll = () => {
    const toSave = [];
    setQuotePricing(prev => {
      const next = { ...prev };
      parts.forEach(p => {
        (p.supplier_quotes ?? []).forEach(q => {
          if (!next[q.id]) return; // only round already-priced quotes
          const rounded = Math.round(next[q.id].sp);
          const cp = next[q.id]._cp ?? Number(q.quoted_price || 0);
          next[q.id] = { ...next[q.id], sp: rounded, gp: gpFromSp(cp, rounded) };
          if (p.selected_quote_id === q.id) toSave.push({ partId: p.id, quoteId: q.id, sp: rounded });
        });
      });
      return next;
    });
    toSave.forEach(({ partId, quoteId, sp }) => patchSellingPrice(partId, quoteId, sp));
  };


  const resetEditing = () => {
    setAddingPartId(null);
    setAddForm(EMPTY_FORM);
    setEditingQuoteId(null);
    setEditForm(EMPTY_FORM);
  };

  return { addingPartId, setAddingPartId, addForm, setAddForm, addSaving, editingQuoteId, setEditingQuoteId, editForm, setEditForm, editSaving, selectingPartId, pricingSaves, overrideConfirm, setOverrideConfirm, overrideNote, setOverrideNote, withOverrideGuard, handleAddQuote, handleEditQuote, onDeleteQuote_original, handleSelectQuote, handleCalculateAll, handleRoundAll, resetEditing };
}
